import { resolveMemberMediaUrl } from '@formosa/shared/utils/mediaUrl';
import { resolveReadMedia } from '../social-post/socialPostMediaModel';
import { coerceDateTime, coerceLocalDate } from '../my-journeys/myJourneysModel';
import { travelMemberProductDisplayName } from '../member-identity/memberIdentityPresentation';

const nonNegative = (value) => Math.max(0, Number(value) || 0);

function snowflakeId(value) {
  if (value == null || value === '') return null;
  const id = String(value);
  return /^[1-9]\d*$/.test(id) ? id : null;
}

function safeDateTime(value) {
  const coerced = coerceDateTime(value);
  if (!coerced) return null;
  if (Number.isNaN(new Date(coerced).getTime())) return null;
  return coerced;
}

export function normalizeCreatorProfile(value) {
  return {
    memberId: snowflakeId(value?.memberId),
    displayName: travelMemberProductDisplayName({
      displayName: value?.displayName,
      username: value?.username,
      nickname: value?.nickname,
    }),
    avatarUrl: resolveMemberMediaUrl(value?.avatarUrl),
    followerCount: nonNegative(value?.followerCount),
    followingCount: nonNegative(value?.followingCount),
    publishedPostCount: nonNegative(value?.publishedPostCount),
    publishedJourneyCount: nonNegative(value?.publishedJourneyCount),
    viewerFollowing: value?.viewerFollowing == null ? null : Boolean(value.viewerFollowing),
  };
}

export function normalizeCreatorPost(value) {
  const journey = value?.linkedJourneyPublication?.available ? value.linkedJourneyPublication : null;
  const quote = value?.quotedPost;
  return {
    postId: snowflakeId(value?.postId),
    postKind: value?.postKind || 'MOOD',
    text: value?.textContent || '',
    coverUrl: resolveMemberMediaUrl(value?.coverFileReference),
    media: resolveReadMedia(value),
    publishedAt: safeDateTime(value?.publishedAt),
    engagement: {
      likeCount: nonNegative(value?.likeCount),
      saveCount: nonNegative(value?.saveCount),
      commentCount: nonNegative(value?.commentCount),
      liked: Boolean(value?.liked),
      saved: Boolean(value?.saved),
    },
    journey: journey ? {
      publicationId: snowflakeId(journey.publicationId),
      title: journey.title || 'Shared journey',
      summary: journey.summary || '',
      coverUrl: resolveMemberMediaUrl(journey.coverFileReference),
    } : null,
    quote: quote ? {
      postId: snowflakeId(quote.postId),
      available: Boolean(quote.available),
      text: quote.available ? (quote.textContent || '') : '',
    } : null,
  };
}

function destinationLabel(raw) {
  if (!raw) return '';
  try {
    const value = typeof raw === 'string' ? JSON.parse(raw) : raw;
    return value?.displayName || value?.name || value?.summary || '';
  } catch { return ''; }
}

export function normalizeCreatorJourney(value) {
  const itinerary = value?.itinerary;
  const hasItinerary = itinerary != null && typeof itinerary === 'object';
  const days = Array.isArray(itinerary?.days) ? itinerary.days : [];
  const unscheduled = Array.isArray(itinerary?.unscheduledActivities) ? itinerary.unscheduledActivities : [];
  const frozen = days.length > 0 || unscheduled.length > 0;
  return {
    publicationId: snowflakeId(value?.publicationId),
    title: value?.title || 'Shared journey',
    summary: value?.summary || '',
    destination: destinationLabel(value?.destinationSummaryJson),
    startDate: coerceLocalDate(value?.startDate),
    endDate: coerceLocalDate(value?.endDate),
    coverUrl: resolveMemberMediaUrl(value?.coverFileReference),
    dayCount: hasItinerary ? (frozen ? days.length : 0) : nonNegative(value?.dayCount),
    activityCount: hasItinerary
      ? (frozen ? days.reduce((total, day) => total + (Array.isArray(day?.activities) ? day.activities.length : 0), 0) + unscheduled.length : 0)
      : nonNegative(value?.activityCount),
    publishedAt: safeDateTime(value?.publishedAt),
  };
}

export function normalizeCreatorPage(payload, itemMapper) {
  return {
    items: Array.isArray(payload?.items) ? payload.items.map(itemMapper) : [],
    nextCursor: payload?.nextCursor || null,
    hasMore: Boolean(payload?.hasMore),
  };
}
